import { Input } from '@aigc/antd';
import { AiDrawer } from '@aigc/components';
import { useAccept, useAutoPaste } from '@aigc/hooks';
import { useOverlayRef } from '@aigc/overlay-hook';
import { InboxOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { App, Button, Form, Radio, Upload, type UploadProps } from 'antd';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { array, number, object, string } from 'yup';
import { FormControl } from '../../../components/form-control';
import { Permission } from '../../common/permision';
import { DepartmentSelect } from '../../departments/components/department-select';
import { createBeforeUpload, validateFile } from '../../file';
import { usePreviewFn } from '../../file/hooks';
import { useAuthority } from '../../me/hook';
import { createSkillMutationOptions, updateSkillMutationOptions } from '../api';
import {
  MAX_SKILL_ATTACHMENT_COUNT,
  SKILL_ATTACHMENT_ACCEPT,
  skillAttachmentSchema,
  skillAttachmentValidator,
  toSkillAttachmentFileList,
  toSkillAttachmentIds,
} from '../attachment';
import {
  SkillScope,
  type SkillDto,
  type SkillMutationInputDto,
} from '../model';

const skill_schema = object({
  scope: number().nullable(),
  department_names: array().of(string().required()).nullable(),
  name: string().trim().min(2).max(30).required('请输入技能名称'),
  description: string().trim().max(200).nullable().default(null),
  instructions: string().trim().max(50000).required('请输入技能指令'),
  attachments: skillAttachmentSchema,
});

export function SkillEditModal(props: { skill?: SkillDto }) {
  const overlay_ref = useOverlayRef<boolean>();
  const { message, notification } = App.useApp();
  const create_skill = useMutation(createSkillMutationOptions);
  const update_skill = useMutation(updateSkillMutationOptions);
  const is_edit = props.skill != null;
  const is_submitting = create_skill.isPending || update_skill.isPending;

  const authority = useAuthority();

  const scopes = useMemo(() => {
    const options = [
      { value: SkillScope.Self, label: '仅自己可见', disabled: false },
    ];

    if (
      authority(Permission.创建部门技能包) ||
      props.skill?.scope === SkillScope.Group
    ) {
      options.unshift({
        value: SkillScope.Group,
        label: '同部门可见',
        disabled: !authority(Permission.创建部门技能包),
      });
    }

    if (
      authority(Permission.创建全局技能包) ||
      props.skill?.scope === SkillScope.Global
    ) {
      options.unshift({
        value: SkillScope.Global,
        label: '全局可见',
        disabled: !authority(Permission.创建全局技能包),
      });
    }

    return options;
  }, [authority, props.skill]);

  const form = useForm({
    defaultValues: {
      scope: props.skill?.scope ?? scopes.at(0)?.value,
      name: props.skill?.name ?? '',
      department_names: props.skill?.department_names,
      description: props.skill?.description ?? null,
      instructions: props.skill?.instructions ?? '',
      attachments: toSkillAttachmentFileList(props.skill?.attachments ?? []),
    },
    resolver: yupResolver(skill_schema),
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    if (is_submitting) return;

    const input: SkillMutationInputDto = {
      name: values.name,
      department_names: !authority([
        Permission.创建全局技能包,
        Permission.查看部门列表,
      ])
        ? void 0
        : values.department_names === void 0
          ? void 0
          : (values.department_names ?? []),
      scope: values.scope ?? void 0,
      description: values.description || null,
      instructions: values.instructions,
      attachment_ids: toSkillAttachmentIds(values.attachments),
    };

    if (props.skill) {
      await update_skill.mutateAsync({ skill_id: props.skill.id, input });
      message.success('技能已更新');
    } else {
      await create_skill.mutateAsync(input);
      message.success('技能已创建');
    }

    overlay_ref.close(true);
  });

  const beforeAttachmentUpload = createBeforeUpload({
    validator: skillAttachmentValidator,
    onError: ({ title, message }) => {
      notification.error({ title, description: message });
    },
  });

  const beforeUpload: UploadProps['beforeUpload'] = async (file, fileList) => {
    const result = await beforeAttachmentUpload(file);
    if (result === Upload.LIST_IGNORE) return result;

    const selected_index = fileList.indexOf(file);
    const precedingResults = await Promise.all(
      fileList
        .slice(0, selected_index)
        .map((selectedFile) =>
          validateFile(selectedFile, skillAttachmentValidator),
        ),
    );
    const valid_preceding_count = precedingResults.filter(
      (error) => error === null,
    ).length;
    const current_count = form.getValues('attachments').length;

    if (current_count + valid_preceding_count >= MAX_SKILL_ATTACHMENT_COUNT) {
      notification.error({
        title: '附件数量超出限制',
        description: `附件最多上传 ${MAX_SKILL_ATTACHMENT_COUNT} 个`,
      });
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const onPreview = usePreviewFn();

  const accept = useAccept(SKILL_ATTACHMENT_ACCEPT);
  useAutoPaste('#uploader', {
    filter: accept.filter,
  });

  const onClose = () => overlay_ref.close();

  return (
    <AiDrawer
      title={is_edit ? '编辑技能' : '新增技能'}
      classNames={{ footer: 'flex gap-2' }}
      size={720}
      mask={{ closable: true }}
      onClose={onClose}
      footer={
        <>
          <div className="flex-auto"></div>
          <Button variant="outlined" color="default" onClick={onClose}>
            取消
          </Button>
          <Button variant="solid" color="primary" onClick={handleSubmit}>
            保存
          </Button>
        </>
      }
    >
      <Form layout="vertical">
        {scopes.length > 1 && (
          <FormControl
            control={form.control}
            name="scope"
            label="范围"
            tooltip="设置当前技能包哪些人可以用"
            render={({ field }) => (
              <Radio.Group {...field} options={scopes}></Radio.Group>
            )}
          ></FormControl>
        )}
        {authority([Permission.创建全局技能包, Permission.查看部门列表]) && (
          <FormControl
            control={form.control}
            name="department_names"
            label="部门"
            render={({ field }) => {
              return (
                <DepartmentSelect
                  {...field}
                  mode="multiple"
                  maxTagCount="responsive"
                  placeholder="默认对所有部门可见"
                ></DepartmentSelect>
              );
            }}
          ></FormControl>
        )}
        <FormControl
          name="name"
          label="名称"
          control={form.control}
          required
          render={({ field }) => <Input {...field} disabled={is_submitting} />}
        />
        <FormControl
          name="description"
          label="说明"
          control={form.control}
          render={({ field }) => (
            <Input.TextArea
              {...field}
              value={field.value ?? ''}
              autoSize={{ minRows: 1, maxRows: 3 }}
              disabled={is_submitting}
            />
          )}
        />
        <FormControl
          name="instructions"
          label="指令"
          tooltip="主要目标和功能是什么？你想要什么风格的回答？"
          control={form.control}
          required
          render={({ field }) => (
            <Input.TextArea
              {...field}
              autoSize={{ minRows: 8, maxRows: 16 }}
              disabled={is_submitting}
            />
          )}
        />
        <FormControl
          name="attachments"
          label="知识"
          control={form.control}
          render={({ field }) => (
            <Upload.Dragger
              id="uploader"
              accept={accept}
              multiple
              maxCount={MAX_SKILL_ATTACHMENT_COUNT}
              disabled={is_submitting}
              beforeUpload={beforeUpload}
              fileList={field.value}
              onPreview={onPreview}
              onChange={({ fileList: file_list }) => {
                const normalized_file_list = file_list.map((file) => {
                  if (!file.response?.file_url) {
                    return file;
                  }

                  return { ...file, url: file.response.file_url };
                });

                field.onChange(normalized_file_list);
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                添加文件，供你的技能在对话中进行参考
              </p>
              <p className="ant-upload-text">
                点击、拖放或<span className="text-blue-500">粘贴</span>
                上传文件
              </p>
            </Upload.Dragger>
          )}
        />
      </Form>
    </AiDrawer>
  );
}
